/* eslint-disable no-use-before-define */
import React, { useState, useContext, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
} from '@fluentui/react-components';
import axios from 'axios';
import DataContext from '../context/data-context';
import DoconIcon from './docon-icon';
import GatsbyLink from './gatsby-link';

const FeedbackFluent = ({ variant = 'default' }) => {
  // Env variables (Gatsby: must start with GATSBY_ to be available in browser)
  const GEO_URL = process.env.GATSBY_GEO_URL || 'https://ipapi.co/json/';
  const FEEDBACK_URL = process.env.GATSBY_FEEDBACK_URL;

  const {
    path,
    settings: { dictionary, footerlinks },
  } = useContext(DataContext);

  const [open, setOpen] = useState(false);
  const [reaction, setReaction] = useState(null);
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reasons, setReasons] = useState([]);

  const toggleReason = (key) =>
    setReasons((prev) =>
      prev.includes(key) ? prev.filter((r) => r !== key) : [...prev, key]
    );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const defaultReaction = window.localStorage.getItem(`reaction:${path}`);
    setReaction(defaultReaction || null);
    setOpen(false);
    setSent(false);
    setMessage('');
    setReasons([]);
  }, [path]);

  const onClickReaction = (val) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(`reaction:${path}`, val);
    }
    setReaction(val);
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    await sendMessageToApi(true);
    setSent(true);
  };

  const sendMessageToApi = async (useMessage) => {
    if (submitting) return;
    setSubmitting(true);

    const nav = typeof window !== 'undefined' ? window.navigator : null;
    const loc = typeof window !== 'undefined' ? window.location : null;
    const doc = typeof document !== 'undefined' ? document : null;

    const payload = {
      type: 'feedback',
      properties: {
        approved: reaction ? reaction === 'yes' : null,
        environment: 'prod',
        language: nav?.language || '',
        ip: '',
        location: '',
        message: useMessage ? message : '',
        url: loc?.href || '',
        reasons: reasons.join(', '),
        pageTitle: doc?.title || '',
        referrer: doc?.referrer || '',
        userAgent: nav?.userAgent || '',
        viewport:
          typeof window !== 'undefined'
            ? `${window.innerWidth}x${window.innerHeight}`
            : '',
        timestamp: new Date().toISOString(),
        path,
      },
    };

    // GEO lookup via ipapi.co
    try {
      if (GEO_URL) {
        console.log('GEO_URL', GEO_URL);
        const { data: geoData } = await axios.get(GEO_URL);

        if (geoData) {
          // ipapi.co fields
          payload.properties.ip = geoData.ip || '';
          payload.properties.location = `${geoData.country_code || ''}, ${
            geoData.city || ''
          }`;
        }
      } else {
        console.warn('GEO_URL is not defined – skipping geo lookup');
      }
    } catch (error) {
      console.error('geo api error', error);
    }

    // Feedback API call
    try {
      if (!FEEDBACK_URL) {
        console.error(
          'FEEDBACK_URL is not defined – cannot send feedback payload'
        );
      } else {
        console.log('FEEDBACK_URL', FEEDBACK_URL);
        await axios.post(FEEDBACK_URL, payload);
      }
    } catch (error) {
      console.error('feedback api error', error);
    } finally {
      setSubmitting(false);
    }
  };

  const {
    feedbackHeadline,
    feedbackReason,
    feedbackMore,
    feedbackSuccess,
    feedbackYes,
    feedbackNo,
    feedbackSubmit,
    feedbackMessagePlaceholder,
    feedbackReasonsPositive,
    feedbackReasonsNegative,
    feedbackSearchHeadline,
    feedbackSearchPlaceholder,
  } = dictionary.feedback;

  const currentReasons =
    reaction === 'no' ? feedbackReasonsNegative : feedbackReasonsPositive;

  const isSearch = variant === 'search';

  // Search variant: inline text field + submit only — no thumbs, no popup.
  if (isSearch) {
    return (
      <div className="feedback feedback-search">
        <h3 style={{ whiteSpace: 'nowrap' }}>{feedbackSearchHeadline}</h3>
        {sent ? (
          <div className="ratings-send-off">
            <h3>{dictionary.feedbackSendoffMessage}</h3>
            <p>{feedbackSuccess}</p>
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            <textarea
              name="text"
              className="textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={feedbackSearchPlaceholder}
              maxLength={300}
            />
            <div className="feedback-modal-actions">
              <button
                className="button small primary"
                type="submit"
                disabled={!message.trim() || submitting}
              >
                {feedbackSubmit}
              </button>
            </div>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="feedback">
      <h3 style={isSearch ? { whiteSpace: 'nowrap' } : undefined}>
        {isSearch ? feedbackSearchHeadline : feedbackHeadline}
      </h3>
      <div className="ratings">
        <button
          type="button"
          className={`thumbs-up ${reaction === 'yes' ? 'selected' : ''}`}
          onClick={() => onClickReaction('yes')}
        >
          <i className="icon icon-thumbs-up-outlined default-state" />
          <i className="icon icon-thumbs-up-filled hover-state" />
          <span>{feedbackYes}</span>
        </button>
        <button
          type="button"
          className={`thumbs-down ${reaction === 'no' ? 'selected' : ''}`}
          onClick={() => onClickReaction('no')}
        >
          <i className="icon icon-thumbs-down-outlined default-state" />
          <i className="icon icon-thumbs-down-filled hover-state" />
          <span>{feedbackNo}</span>
        </button>
      </div>

      <Dialog open={open} onOpenChange={(_, d) => setOpen(d.open)}>
        <DialogSurface className="feedback-modal theme">
          <button className="icon-cross cross" onClick={() => setOpen(false)} />
          {sent ? (
            <div className="ratings-send-off">
              <h3>{dictionary.feedbackSendoffMessage}</h3>
              <p>{feedbackSuccess}</p>
            </div>
          ) : (
            <form onSubmit={onSubmit}>
              <DialogBody>
                <h3 style={isSearch ? { whiteSpace: 'nowrap' } : undefined}>
                  {isSearch ? feedbackSearchHeadline : feedbackHeadline}
                </h3>
                <DialogContent>
                  {!isSearch && (
                    <>
                      <div className="feedback-modal-ratings">
                        <button
                          type="button"
                          className={`button ${
                            reaction === 'yes'
                              ? 'button-feedback active'
                              : 'button-feedback'
                          }`}
                          onClick={() => onClickReaction('yes')}
                        >
                          <DoconIcon
                            className={`theme ${
                              reaction === 'yes' ? 'reaction-active' : ''
                            }`}
                            name={reaction === 'yes' ? 'like-solid' : 'like'}
                            size="18"
                          />
                          <span>{feedbackYes}</span>
                        </button>
                        <button
                          type="button"
                          className={`button ${
                            reaction === 'no'
                              ? 'button-feedback active'
                              : 'button-feedback'
                          }`}
                          onClick={() => onClickReaction('no')}
                        >
                          <DoconIcon
                            className={`theme ${
                              reaction === 'no' ? 'reaction-active' : ''
                            }`}
                            name={reaction === 'no' ? 'dislike-solid' : 'dislike'}
                            size="18"
                          />
                          <span>{feedbackNo}</span>
                        </button>
                      </div>

                      <h6>{feedbackReason}</h6>
                      <div style={{ display: 'grid', gap: 6 }}>
                        {currentReasons.map((text) => (
                          <label
                            className="checkmark checkmark-filter"
                            key={text}
                          >
                            <input
                              type="checkbox"
                              checked={reasons.includes(text)}
                              onChange={() => toggleReason(text)}
                            />
                            {text}
                          </label>
                        ))}
                      </div>

                      <h6>{feedbackMore}</h6>
                    </>
                  )}

                  <textarea
                    name="text"
                    className="textarea"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={
                      isSearch
                        ? feedbackSearchPlaceholder
                        : feedbackMessagePlaceholder
                    }
                    maxLength={300}
                  />
                </DialogContent>
              </DialogBody>

              <div className="feedback-modal-actions">
                <button
                  className="button small primary"
                  type="submit"
                  disabled={
                    isSearch
                      ? !message.trim() || submitting
                      : reasons.length === 0 || submitting
                  }
                >
                  {feedbackSubmit}
                </button>

                {footerlinks.length > 0 &&
                  (() => {
                    const lastLink = footerlinks[footerlinks.length - 1];
                    return (
                      <a href={lastLink.href}>
                        <GatsbyLink to={lastLink.href}>
                          <span>{lastLink.name}</span>
                        </GatsbyLink>
                      </a>
                    );
                  })()}
              </div>
            </form>
          )}
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default FeedbackFluent;
